import type { TableColumnsType, TableProps } from "antd";
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

const ACTION_COLUMN_KEYS = new Set(["action", "actions"]);
const ACTION_COLUMN_TITLES = new Set(["Thao tác"]);

type ColumnLike = {
  title?: unknown;
  key?: React.Key;
  fixed?: "left" | "right";
};

export const isActionColumn = (column: ColumnLike) => {
  if (column.key && ACTION_COLUMN_KEYS.has(String(column.key))) {
    return true;
  }

  const { title } = column;

  if (typeof title === "string" && ACTION_COLUMN_TITLES.has(title)) {
    return true;
  }

  return false;
};

export const applyFixedActionColumn = <T extends object>(
  columns: TableColumnsType<T> | undefined,
  enabled: boolean,
): TableColumnsType<T> | undefined => {
  if (!enabled || !columns?.length) {
    return columns;
  }

  return columns.map((column) => {
    if ("children" in column) {
      return column;
    }

    return isActionColumn(column as ColumnLike) && !column.fixed
      ? { ...column, fixed: "right" as const }
      : column;
  });
};

export const applyFixedActionToChildren = (
  children: ReactNode,
  enabled: boolean,
): ReactNode => {
  if (!enabled || !children) {
    return children;
  }

  return Children.map(children, (child) => {
    if (!isValidElement(child)) {
      return child;
    }

    const props = child.props as ColumnLike;

    if (isActionColumn(props) && !props.fixed) {
      return cloneElement(child as ReactElement, { fixed: "right" });
    }

    return child;
  });
};

export const resolveScrollForFixed = (
  scroll: TableProps["scroll"],
  enabled: boolean,
): TableProps["scroll"] => {
  if (!enabled) {
    return scroll;
  }

  if (scroll?.x != null) {
    return scroll;
  }

  return { ...scroll, x: "max-content" };
};
